//@ts-nocheck
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check } from "lucide-react";
import { useKoreaLanguage } from "./koreaLanguage";
import { L, ls } from "../../utils/lnText";

// ─── Localization ───────────────────────────────────────────────────────────
// Korean when the user's preferred language is "ko", English otherwise (see
// koreaLanguage.ts).
const useIsKorean = () => useKoreaLanguage() === "ko";

// ─── Desktop detection ────────────────────────────────────────────────────────
const useIsDesktop = (breakpoint = 768) => {
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== "undefined" ? window.innerWidth >= breakpoint : false,
  );
  useEffect(() => {
    const handler = () => setIsDesktop(window.innerWidth >= breakpoint);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, [breakpoint]);
  return isDesktop;
};

// ─── Types ───────────────────────────────────────────────────────────────────

interface Korea3BoxData {
  name: string | null;
  gender: string | null;
  blood_type: string | null;
  dob: string | null;
  destiny_time: string | null;
  birth_time: string | null;
  birth_city: string | null;
}

interface Korea3BoxProps {
  onSubmit: (selfData: Korea3BoxData, partnerData: Korea3BoxData) => void;
  isLoading?: boolean;
  categoryName?: string;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const KR_FONT = "'Noto Sans KR', sans-serif";

// K-Soft Korean theme colors: warm beige, muted coral, soft brown
const COLORS = {
  // Dark theme (mobile) - Korean night aesthetic
  bg: "linear-gradient(160deg, #1a1520 0%, #0d0b12 100%)",
  card: "rgba(255,255,255,0.04)",
  cardBorder: "rgba(255,255,255,0.09)",
  cardBorderWarm: "rgba(180,160,140,0.55)",
  warmGold: "#C4A574",
  warmGoldSoft: "rgba(196,165,116,0.18)",
  white: "#fff",
  whiteDim: "rgba(255,255,255,0.65)",
  whiteVeryDim: "rgba(255,255,255,0.38)",
  inputBg: "rgba(255,255,255,0.06)",
  inputBorder: "rgba(255,255,255,0.12)",
  accent: "rgba(196,165,116,0.55)",
  btnGrad: "linear-gradient(135deg, #C4A574 0%, #8B7355 100%)",
  btnGradWarm: "linear-gradient(135deg, #C4A574 0%, #6B5344 100%)",
  resultBg: "rgba(196,165,116,0.06)",
  // Light theme (desktop)
  lightBg: "linear-gradient(160deg, #FAF7F2 0%, #F0EBE3 100%)",
  lightCard: "#fff",
  lightCardBorder: "#E8E2D9",
  lightCardBorderActive: "#C4A574",
  lightAmethyst: "#8B7355",
  lightWarm: "#C4A574",
  lightWarmSoft: "rgba(196,165,116,0.08)",
  textDark: "#4A4238",
  textMuted: "#9CA3AF",
  textLight: "#8B7355",
  lightWhite: "#fff",
  lightInputBg: "#fff",
  lightInputBorder: "#E8E2D9",
  lightShadow: "0 2px 12px rgba(139,115,85,0.08)",
  lightShadowLg: "0 4px 24px rgba(139,115,85,0.14)",
};

const BLOOD_TYPES = ["A", "B", "O", "AB"];

const buildMonths = (isKorean: boolean) =>
  Array.from({ length: 12 }, (_, i) => ({
    value: String(i + 1).padStart(2, "0"),
    label: isKorean ? `${i + 1}월` : String(i + 1),
  }));

const buildDays = (isKorean: boolean) =>
  Array.from({ length: 31 }, (_, i) => ({
    value: String(i + 1).padStart(2, "0"),
    label: isKorean ? `${i + 1}일` : String(i + 1),
  }));

const buildYears = (isKorean: boolean) =>
  Array.from({ length: 100 }, (_, i) => {
    const y = new Date().getFullYear() - i;
    return { value: String(y), label: isKorean ? `${y}년` : String(y) };
  });

const buildHours = (isKorean: boolean) =>
  Array.from({ length: 24 }, (_, i) => ({
    value: String(i).padStart(2, "0"),
    label: isKorean ? `${i}시` : String(i).padStart(2, "0"),
  }));

const buildMinutes5 = (isKorean: boolean) =>
  Array.from({ length: 12 }, (_, i) => ({
    value: String(i * 5).padStart(2, "0"),
    label: isKorean
      ? `${String(i * 5).padStart(2, "0")}분`
      : String(i * 5).padStart(2, "0"),
  }));

// ─── Slide-Up Panel ──────────────────────────────────────────────────────────

interface SlideUpPanelProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  isDesktop?: boolean;
}

const SlideUpPanel: React.FC<SlideUpPanelProps> = ({
  isOpen,
  onClose,
  title,
  children,
  isDesktop,
}) => {
  const theme = "light"; // Forced light theme
  const bg = "linear-gradient(180deg, #FAF7F2 0%, #F0EBE3 100%)";
  const textColor = COLORS.textDark;
  const textMuted = COLORS.textMuted;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.55)",
              backdropFilter: "blur(4px)",
              zIndex: 9998,
            }}
          />
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{
              type: "spring",
              damping: 30,
              stiffness: 320,
              duration: 0.25,
            }}
            style={{
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              maxHeight: "70vh",
              background: bg,
              borderRadius: "20px 20px 0 0",
              padding: "24px 20px 32px",
              zIndex: 9999,
              overflowY: "auto",
              scrollbarWidth: "none",
              fontFamily: KR_FONT,
            }}
          >
            {/* Handle */}
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginBottom: 16,
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 4,
                  borderRadius: 2,
                  background: "rgba(0,0,0,0.12)",
                }}
              />
            </div>
            {/* Header */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 20,
              }}
            >
              <p
                style={{
                  fontSize: 16,
                  fontWeight: 500,
                  color: textColor,
                  fontFamily: KR_FONT,
                  margin: 0,
                }}
              >
                {title}
              </p>
              <button
                onClick={onClose}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: textMuted,
                  padding: 4,
                }}
              >
                <X size={18} />
              </button>
            </div>
            {children}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

// ─── Destiny Box Card ─────────────────────────────────────────────────────────

interface DestinyBoxProps {
  icon: string;
  labelKR: string;
  labelEN: string;
  isCompleted: boolean;
  onClick: () => void;
  isDesktop?: boolean;
}

const DestinyBox: React.FC<DestinyBoxProps> = ({
  icon,
  labelKR,
  labelEN,
  isCompleted,
  onClick,
  isDesktop,
}) => {
  const theme = "light"; // Forced light theme
  const cardBg = COLORS.lightCard;
  const borderColor = isCompleted
    ? COLORS.lightCardBorderActive
    : COLORS.lightCardBorder;
  const textColor = isCompleted ? COLORS.lightWarm : COLORS.textDark;
  const textMuted = COLORS.textMuted;

  return (
    <motion.button
      onClick={onClick}
      whileHover={{ scale: 1.03 }}
      whileTap={{ scale: 0.97 }}
      style={{
        flex: 1,
        minWidth: 0,
        height: 110,
        borderRadius: 14,
        background: isCompleted ? COLORS.lightWarmSoft : cardBg,
        border: `1px solid ${borderColor}`,
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        padding: "10px 8px",
        position: "relative",
        transition: "border-color 0.2s, background 0.2s",
        boxShadow: isCompleted ? `0 0 14px rgba(196,165,116,0.15)` : "none",
        fontFamily: KR_FONT,
      }}
    >
      {isCompleted && (
        <div
          style={{
            position: "absolute",
            top: 7,
            right: 7,
            width: 18,
            height: 18,
            borderRadius: "50%",
            background: COLORS.lightWarm,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Check size={11} color="#fff" strokeWidth={3} />
        </div>
      )}
      <span style={{ fontSize: 26 }}>{icon}</span>
      <p
        style={{
          fontSize: 11,
          color: textColor,
          fontFamily: KR_FONT,
          margin: 0,
          textAlign: "center",
          lineHeight: 1.35,
        }}
      >
        {labelKR}
      </p>
      <p
        style={{
          fontSize: 10,
          color: textMuted,
          fontFamily: KR_FONT,
          margin: 0,
        }}
      >
        {labelEN}
      </p>
    </motion.button>
  );
};

// ─── Select Row ───────────────────────────────────────────────────────────────

interface SelectRowProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  isDesktop?: boolean;
}

const SelectRow: React.FC<SelectRowProps> = ({
  label,
  value,
  onChange,
  options,
  isDesktop,
}) => {
  const theme = "light"; // Forced light theme
  const textColor = COLORS.textDark;
  const borderColor = COLORS.lightInputBorder;
  const inputBg = COLORS.lightInputBg;
  const optionBg = "#fff";

  return (
    <div style={{ marginBottom: 14 }}>
      <p
        style={{
          fontSize: 13,
          color: textColor,
          fontFamily: KR_FONT,
          marginBottom: 6,
        }}
      >
        {label}
      </p>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: "100%",
          height: 44,
          borderRadius: 10,
          border: `1px solid ${borderColor}`,
          background: inputBg,
          color: value ? COLORS.textDark : COLORS.textMuted,
          padding: "0 12px",
          fontSize: 15,
          fontFamily: KR_FONT,
          outline: "none",
          appearance: "none",
          WebkitAppearance: "none",
          cursor: "pointer",
        }}
      >
        <option
          value=""
          style={{ background: optionBg, color: COLORS.textMuted }}
        >
          —
        </option>
        {options.map((o) => (
          <option
            key={o.value}
            value={o.value}
            style={{ background: optionBg }}
          >
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
};

// ─── Section Title ────────────────────────────────────────────────────────────

interface SectionTitleProps {
  kr: string;
  en: string;
  isDesktop?: boolean;
}

const SectionTitle: React.FC<SectionTitleProps> = ({ kr, en, isDesktop }) => {
  const theme = "light"; // Forced light theme
  const textColor = COLORS.textDark;
  const textMuted = COLORS.textMuted;

  return (
    <div style={{ marginBottom: 16 }}>
      <p
        style={{
          fontSize: 15,
          fontWeight: 500,
          color: textColor,
          fontFamily: KR_FONT,
          margin: 0,
        }}
      >
        {kr}
      </p>
      <p
        style={{
          fontSize: 12,
          color: textMuted,
          fontFamily: KR_FONT,
          margin: "2px 0 0",
        }}
      >
        {en}
      </p>
    </div>
  );
};

// ─── Result Section Card ──────────────────────────────────────────────────────

interface ResultSectionCardProps {
  titleKR: string;
  titleEN: string;
  text: string;
  icon: string;
  isDesktop?: boolean;
}

const ResultSectionCard: React.FC<ResultSectionCardProps> = ({
  titleKR,
  titleEN,
  text,
  icon,
  isDesktop,
}) => {
  const theme = "light"; // Forced light theme
  const borderColor = COLORS.lightCardBorderActive;
  const titleColor = COLORS.lightWarm;
  const textColor = COLORS.textDark;
  const textMuted = COLORS.textMuted;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      style={{
        borderRadius: 14,
        background: COLORS.lightWarmSoft,
        border: `1px solid ${borderColor}`,
        padding: "16px 18px",
        marginBottom: 12,
        fontFamily: KR_FONT,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 10,
        }}
      >
        <span style={{ fontSize: 18 }}>{icon}</span>
        <div>
          <p
            style={{
              fontSize: 14,
              fontWeight: 500,
              color: titleColor,
              margin: 0,
              fontFamily: KR_FONT,
            }}
          >
            {titleKR}
          </p>
          <p
            style={{
              fontSize: 11,
              color: textMuted,
              margin: "1px 0 0",
              fontFamily: KR_FONT,
            }}
          >
            {titleEN}
          </p>
        </div>
      </div>
      <p
        style={{
          fontSize: 14,
          color: textColor,
          lineHeight: 1.7,
          margin: 0,
          fontFamily: KR_FONT,
        }}
      >
        {text}
      </p>
    </motion.div>
  );
};

// ─── Loading Dots ─────────────────────────────────────────────────────────────

const LoadingDots: React.FC<{ isDesktop?: boolean }> = ({ isDesktop }) => {
  const dotColor = COLORS.lightWarm;
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        gap: 8,
        padding: "32px 0",
      }}
    >
      {[0, 1, 2].map((i) => (
        <motion.div
          key={i}
          animate={{ y: [0, -8, 0] }}
          transition={{
            duration: 0.9,
            repeat: Infinity,
            delay: i * 0.18,
            ease: "easeInOut",
          }}
          style={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            background: dotColor,
          }}
        />
      ))}
    </div>
  );
};

// ─── Primary Button ───────────────────────────────────────────────────────────

interface PrimaryBtnProps {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  icon?: string;
  isDesktop?: boolean;
}

const PrimaryBtn: React.FC<PrimaryBtnProps> = ({
  label,
  onClick,
  disabled,
  loading,
  icon,
  isDesktop,
}) => {
  const theme = "light"; // Forced light theme
  const bg = disabled ? "rgba(196,165,116,0.15)" : COLORS.btnGradWarm;
  const textColor = disabled ? COLORS.textMuted : COLORS.lightWhite;
  const shadow = disabled ? "none" : COLORS.lightShadowLg;

  return (
    <motion.button
      whileTap={{ scale: disabled ? 1 : 0.97 }}
      onClick={onClick}
      disabled={disabled || loading}
      style={{
        width: "100%",
        height: 54,
        borderRadius: 14,
        background: bg,
        color: textColor,
        fontSize: 15,
        fontWeight: 600,
        fontFamily: KR_FONT,
        border: "none",
        cursor: disabled ? "not-allowed" : "pointer",
        boxShadow: shadow,
        transition: "all 0.2s",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
      }}
    >
      {loading ? (
        <LoadingDots isDesktop={isDesktop} />
      ) : (
        <>
          {icon && <span>{icon}</span>}
          {label}
        </>
      )}
    </motion.button>
  );
};

// ─── Localized copy ───────────────────────────────────────────────────────────

const buildCopy = (isKorean: boolean) =>
  isKorean
    ? {
        stepSelf: "나의 정보",
        stepPartner: "상대의 정보",
        selfHeading: "당신의 세 가지 키",
        selfSubheading: "Three Keys of Your Destiny",
        partnerHeading: "상대의 세 가지 키",
        partnerSubheading: "Partner Three Keys",
        nameLabel: "이름 (이름을 입력해주세요)",
        namePlaceholder: "이름",
        partnerNameLabel: "상대 이름 (이름을 입력해주세요)",
        partnerNamePlaceholder: "상대 이름",
        genderLabel: "성별 (선택해주세요)",
        genderOptions: ["남성", "여성", "기타"],
        bloodTypeLabelKR: "혈액형 분위기",
        bloodTypeLabelEN: "Blood Type",
        partnerBloodTypeLabelKR: "상대의 혈액형",
        birthDayLabelKR: "태어난 날의 기운",
        birthDayLabelEN: "Birth-Day",
        partnerBirthDayLabelKR: "상대의 생일",
        destinyTimeLabelKR: "시간의 흐름",
        destinyTimeLabelEN: "Destiny Time",
        partnerDestinyTimeLabelKR: "상대의 시간",
        continueButton: "상대와의 궁합 보기",
        backButton: "← 돌아가기",
        submitButton: "두 사람의 궁합 보기",
        loadingText: "두 사람의 궁합을 읽고 있어요...",
        // Panels
        bloodPanelTitle: "혈액형 분위기",
        bloodPanelPrompt: "혈액형을 선택해주세요",
        bloodPanelFooter: "Blood Type Atmosphere",
        partnerBloodPanelTitle: "상대의 혈액형",
        partnerBloodPanelPrompt: "상대의 혈액형을 선택해주세요",
        partnerBloodPanelFooter: "Partner Blood Type",
        dobPanelTitle: "태어난 날의 기운",
        dobSectionKR: "생년월일",
        dobSectionEN: "Date of Birth",
        partnerDobPanelTitle: "상대의 생일",
        partnerDobSectionKR: "상대의 생년월일",
        partnerDobSectionEN: "Partner Date of Birth",
        yearLabel: "년 (Year)",
        monthLabel: "월 (Month)",
        dayLabel: "일 (Day)",
        hourLabel: "시 (Hour)",
        minuteLabel: "분 (Minute)",
        timePanelTitle: "당신의 시간 흐름",
        timeSectionKR: "태어난 시간",
        timeSectionEN: "Birth Time (optional)",
        citySectionKR: "출생지",
        citySectionEN: "Birth City (optional)",
        cityPlaceholder: "도시 / City",
        partnerTimePanelTitle: "상대의 시간 흐름",
        partnerTimeSectionKR: "상대의 태어난 시간",
        partnerTimeSectionEN: "Partner Birth Time (optional)",
        partnerCitySectionKR: "상대의 출생지",
        partnerCitySectionEN: "Partner Birth City (optional)",
      }
    : L({
        stepSelf: "My Info",
        stepPartner: "Partner Info",
        selfHeading: "Three Keys of Your Destiny",
        selfSubheading: "당신의 세 가지 키",
        partnerHeading: "Partner Three Keys",
        partnerSubheading: "상대의 세 가지 키",
        nameLabel: "Name (please enter your name)",
        namePlaceholder: "Name",
        partnerNameLabel: "Partner Name (please enter their name)",
        partnerNamePlaceholder: "Partner Name",
        genderLabel: "Gender (please select)",
        genderOptions: ["Male", "Female", "Other"],
        bloodTypeLabelKR: "Blood Type",
        bloodTypeLabelEN: "혈액형 분위기",
        partnerBloodTypeLabelKR: "Partner Blood Type",
        birthDayLabelKR: "Birth-Day",
        birthDayLabelEN: "태어난 날의 기운",
        partnerBirthDayLabelKR: "Partner Birth-Day",
        destinyTimeLabelKR: "Destiny Time",
        destinyTimeLabelEN: "시간의 흐름",
        partnerDestinyTimeLabelKR: "Partner Destiny Time",
        continueButton: "View Compatibility with Partner",
        backButton: "← Back",
        submitButton: "View Compatibility",
        loadingText: "Reading your compatibility...",
        // Panels
        bloodPanelTitle: "Blood Type",
        bloodPanelPrompt: "Please select your blood type",
        bloodPanelFooter: "Blood Type Atmosphere",
        partnerBloodPanelTitle: "Partner Blood Type",
        partnerBloodPanelPrompt: "Please select their blood type",
        partnerBloodPanelFooter: "Partner Blood Type",
        dobPanelTitle: "Birth-Day",
        dobSectionKR: "Date of Birth",
        dobSectionEN: "생년월일",
        partnerDobPanelTitle: "Partner Birth-Day",
        partnerDobSectionKR: "Partner Date of Birth",
        partnerDobSectionEN: "상대의 생년월일",
        yearLabel: "Year",
        monthLabel: "Month",
        dayLabel: "Day",
        hourLabel: "Hour",
        minuteLabel: "Minute",
        timePanelTitle: "Your Destiny Time",
        timeSectionKR: "Birth Time (optional)",
        timeSectionEN: "태어난 시간",
        citySectionKR: "Birth City (optional)",
        citySectionEN: "출생지",
        cityPlaceholder: "City",
        partnerTimePanelTitle: "Partner Destiny Time",
        partnerTimeSectionKR: "Partner Birth Time (optional)",
        partnerTimeSectionEN: "상대의 태어난 시간",
        partnerCitySectionKR: "Partner Birth City (optional)",
        partnerCitySectionEN: "상대의 출생지",
      });

// ─── Main Component ───────────────────────────────────────────────────────────

const KoreaCompatibility3Box: React.FC<Korea3BoxProps> = ({
  onSubmit,
  isLoading,
  categoryName,
}) => {
  const isDesktop = useIsDesktop();
  const isKorean = useIsKorean();
  const t = buildCopy(isKorean);
  const MONTHS = buildMonths(isKorean);
  const DAYS = buildDays(isKorean);
  const YEARS = buildYears(isKorean);
  const HOURS = buildHours(isKorean);
  const MINUTES_5 = buildMinutes5(isKorean);
  const theme = "light"; // Forced light theme globally to match desktop

  // Self state
  const [selfName, setSelfName] = useState("");
  const [selfGender, setSelfGender] = useState("");
  const [selfBloodType, setSelfBloodType] = useState("");
  const [selfDobY, setSelfDobY] = useState("");
  const [selfDobM, setSelfDobM] = useState("");
  const [selfDobD, setSelfDobD] = useState("");
  const [selfTimeH, setSelfTimeH] = useState("");
  const [selfTimeMin, setSelfTimeMin] = useState("");
  const [selfCity, setSelfCity] = useState("");

  // Partner state
  const [partnerName, setPartnerName] = useState("");
  const [partnerGender, setPartnerGender] = useState("");
  const [partnerBloodType, setPartnerBloodType] = useState("");
  const [partnerDobY, setPartnerDobY] = useState("");
  const [partnerDobM, setPartnerDobM] = useState("");
  const [partnerDobD, setPartnerDobD] = useState("");
  const [partnerTimeH, setPartnerTimeH] = useState("");
  const [partnerTimeMin, setPartnerTimeMin] = useState("");
  const [partnerCity, setPartnerCity] = useState("");

  // UI state
  const [step, setStep] = useState<"self" | "partner">("self");
  const [showSelfPanel, setShowSelfPanel] = useState(false);
  const [showPartnerPanel, setShowPartnerPanel] = useState(false);
  const [activeSelfBox, setActiveSelfBox] = useState<"blood" | "dob" | "time">(
    "blood",
  );
  const [activePartnerBox, setActivePartnerBox] = useState<
    "blood" | "dob" | "time"
  >("blood");

  // Self DOB string
  const selfDob =
    selfDobY && selfDobM && selfDobD
      ? `${selfDobD}/${selfDobM}/${selfDobY}`
      : null;
  const selfDestinyTime = selfTimeH
    ? `${selfTimeH}:${selfTimeMin || "00"}`
    : null;
  const selfComplete = !!(selfBloodType || selfDob);

  // Partner DOB string
  const partnerDob =
    partnerDobY && partnerDobM && partnerDobD
      ? `${partnerDobD}/${partnerDobM}/${partnerDobY}`
      : null;
  const partnerDestinyTime = partnerTimeH
    ? `${partnerTimeH}:${partnerTimeMin || "00"}`
    : null;
  const partnerComplete = !!(partnerBloodType || partnerDob);

  // Step completeness
  const selfReady = !!(selfBloodType && selfDob);
  const partnerReady = !!(partnerBloodType && partnerDob);

  const bg = COLORS.lightBg;
  const textColor = COLORS.textDark;
  const textMuted = COLORS.textMuted;

  const handleSubmit = () => {
    const selfData: Korea3BoxData = {
      name: selfName || null,
      gender: selfGender || null,
      blood_type: selfBloodType || null,
      dob: selfDob,
      destiny_time: selfDestinyTime,
      birth_time: selfDestinyTime,
      birth_city: selfCity || null,
    };
    const partnerData: Korea3BoxData = {
      name: partnerName || null,
      gender: partnerGender || null,
      blood_type: partnerBloodType || null,
      dob: partnerDob,
      destiny_time: partnerDestinyTime,
      birth_time: partnerDestinyTime,
      birth_city: partnerCity || null,
    };
    onSubmit(selfData, partnerData);
  };

  // ─── Self Blood Type Panel ───
  const SelfBloodTypePanel = () => (
    <SlideUpPanel
      isOpen={showSelfPanel && activeSelfBox === "blood"}
      onClose={() => setShowSelfPanel(false)}
      title={t.bloodPanelTitle}
      isDesktop={isDesktop}
    >
      <p
        style={{
          fontSize: 14,
          color: textMuted,
          marginBottom: 20,
          fontFamily: KR_FONT,
        }}
      >
        {t.bloodPanelPrompt}
      </p>
      <div style={{ display: "flex", gap: 10 }}>
        {BLOOD_TYPES.map((bt) => {
          const active = selfBloodType === bt;
          return (
            <motion.button
              key={bt}
              whileTap={{ scale: 0.93 }}
              onClick={() => {
                setSelfBloodType(bt);
                setShowSelfPanel(false);
              }}
              style={{
                flex: 1,
                height: 52,
                borderRadius: 12,
                border: `2px solid ${active ? COLORS.lightWarm : COLORS.lightCardBorder}`,
                background: active ? COLORS.lightWarmSoft : "transparent",
                color: active ? COLORS.lightWarm : textColor,
                fontSize: 16,
                fontWeight: active ? 700 : 500,
                cursor: "pointer",
                transition: "all 0.18s",
                fontFamily: KR_FONT,
              }}
            >
              {bt}
            </motion.button>
          );
        })}
      </div>
      <p
        style={{
          fontSize: 12,
          color: textMuted,
          marginTop: 16,
          textAlign: "center",
          fontFamily: KR_FONT,
        }}
      >
        {t.bloodPanelFooter}
      </p>
    </SlideUpPanel>
  );

  // ─── Self DOB Panel ───
  const SelfDobPanel = () => (
    <SlideUpPanel
      isOpen={showSelfPanel && activeSelfBox === "dob"}
      onClose={() => setShowSelfPanel(false)}
      title={t.dobPanelTitle}
      isDesktop={isDesktop}
    >
      <SectionTitle
        kr={t.dobSectionKR}
        en={t.dobSectionEN}
        isDesktop={isDesktop}
      />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "2fr 1fr 1fr",
          gap: 8,
          marginBottom: 20,
        }}
      >
        <SelectRow
          label={t.yearLabel}
          value={selfDobY}
          onChange={setSelfDobY}
          options={YEARS}
          isDesktop={isDesktop}
        />
        <SelectRow
          label={t.monthLabel}
          value={selfDobM}
          onChange={setSelfDobM}
          options={MONTHS}
          isDesktop={isDesktop}
        />
        <SelectRow
          label={t.dayLabel}
          value={selfDobD}
          onChange={setSelfDobD}
          options={DAYS}
          isDesktop={isDesktop}
        />
      </div>
    </SlideUpPanel>
  );

  // ─── Self Time Panel ───
  const SelfTimePanel = () => (
    <SlideUpPanel
      isOpen={showSelfPanel && activeSelfBox === "time"}
      onClose={() => setShowSelfPanel(false)}
      title={t.timePanelTitle}
      isDesktop={isDesktop}
    >
      <SectionTitle
        kr={t.timeSectionKR}
        en={t.timeSectionEN}
        isDesktop={isDesktop}
      />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 8,
          marginBottom: 20,
        }}
      >
        <SelectRow
          label={t.hourLabel}
          value={selfTimeH}
          onChange={setSelfTimeH}
          options={HOURS}
          isDesktop={isDesktop}
        />
        <SelectRow
          label={t.minuteLabel}
          value={selfTimeMin}
          onChange={setSelfTimeMin}
          options={MINUTES_5}
          isDesktop={isDesktop}
        />
      </div>
      <SectionTitle
        kr={t.citySectionKR}
        en={t.citySectionEN}
        isDesktop={isDesktop}
      />
      <input
        type="text"
        value={selfCity}
        onChange={(e) => setSelfCity(e.target.value)}
        placeholder={t.cityPlaceholder}
        style={{
          width: "100%",
          height: 44,
          borderRadius: 10,
          border: `1px solid ${COLORS.lightInputBorder}`,
          background: COLORS.lightInputBg,
          color: COLORS.textDark,
          padding: "0 12px",
          fontSize: 14,
          fontFamily: KR_FONT,
          outline: "none",
          boxSizing: "border-box",
        }}
      />
    </SlideUpPanel>
  );

  // ─── Partner Blood Type Panel ───
  const PartnerBloodTypePanel = () => (
    <SlideUpPanel
      isOpen={showPartnerPanel && activePartnerBox === "blood"}
      onClose={() => setShowPartnerPanel(false)}
      title={t.partnerBloodPanelTitle}
      isDesktop={isDesktop}
    >
      <p
        style={{
          fontSize: 14,
          color: textMuted,
          marginBottom: 20,
          fontFamily: KR_FONT,
        }}
      >
        {t.partnerBloodPanelPrompt}
      </p>
      <div style={{ display: "flex", gap: 10 }}>
        {BLOOD_TYPES.map((bt) => {
          const active = partnerBloodType === bt;
          return (
            <motion.button
              key={bt}
              whileTap={{ scale: 0.93 }}
              onClick={() => {
                setPartnerBloodType(bt);
                setShowPartnerPanel(false);
              }}
              style={{
                flex: 1,
                height: 52,
                borderRadius: 12,
                border: `2px solid ${active ? COLORS.lightWarm : COLORS.lightCardBorder}`,
                background: active ? COLORS.lightWarmSoft : "transparent",
                color: active ? COLORS.lightWarm : textColor,
                fontSize: 16,
                fontWeight: active ? 700 : 500,
                cursor: "pointer",
                transition: "all 0.18s",
                fontFamily: KR_FONT,
              }}
            >
              {bt}
            </motion.button>
          );
        })}
      </div>
      <p
        style={{
          fontSize: 12,
          color: textMuted,
          marginTop: 16,
          textAlign: "center",
          fontFamily: KR_FONT,
        }}
      >
        {t.partnerBloodPanelFooter}
      </p>
    </SlideUpPanel>
  );

  // ─── Partner DOB Panel ───
  const PartnerDobPanel = () => (
    <SlideUpPanel
      isOpen={showPartnerPanel && activePartnerBox === "dob"}
      onClose={() => setShowPartnerPanel(false)}
      title={t.partnerDobPanelTitle}
      isDesktop={isDesktop}
    >
      <SectionTitle
        kr={t.partnerDobSectionKR}
        en={t.partnerDobSectionEN}
        isDesktop={isDesktop}
      />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "2fr 1fr 1fr",
          gap: 8,
          marginBottom: 20,
        }}
      >
        <SelectRow
          label={t.yearLabel}
          value={partnerDobY}
          onChange={setPartnerDobY}
          options={YEARS}
          isDesktop={isDesktop}
        />
        <SelectRow
          label={t.monthLabel}
          value={partnerDobM}
          onChange={setPartnerDobM}
          options={MONTHS}
          isDesktop={isDesktop}
        />
        <SelectRow
          label={t.dayLabel}
          value={partnerDobD}
          onChange={setPartnerDobD}
          options={DAYS}
          isDesktop={isDesktop}
        />
      </div>
    </SlideUpPanel>
  );

  // ─── Partner Time Panel ───
  const PartnerTimePanel = () => (
    <SlideUpPanel
      isOpen={showPartnerPanel && activePartnerBox === "time"}
      onClose={() => setShowPartnerPanel(false)}
      title={t.partnerTimePanelTitle}
      isDesktop={isDesktop}
    >
      <SectionTitle
        kr={t.partnerTimeSectionKR}
        en={t.partnerTimeSectionEN}
        isDesktop={isDesktop}
      />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 8,
          marginBottom: 20,
        }}
      >
        <SelectRow
          label={t.hourLabel}
          value={partnerTimeH}
          onChange={setPartnerTimeH}
          options={HOURS}
          isDesktop={isDesktop}
        />
        <SelectRow
          label={t.minuteLabel}
          value={partnerTimeMin}
          onChange={setPartnerTimeMin}
          options={MINUTES_5}
          isDesktop={isDesktop}
        />
      </div>
      <SectionTitle
        kr={t.partnerCitySectionKR}
        en={t.partnerCitySectionEN}
        isDesktop={isDesktop}
      />
      <input
        type="text"
        value={partnerCity}
        onChange={(e) => setPartnerCity(e.target.value)}
        placeholder={t.cityPlaceholder}
        style={{
          width: "100%",
          height: 44,
          borderRadius: 10,
          border: `1px solid ${COLORS.lightInputBorder}`,
          background: COLORS.lightInputBg,
          color: COLORS.textDark,
          padding: "0 12px",
          fontSize: 14,
          fontFamily: KR_FONT,
          outline: "none",
          boxSizing: "border-box",
        }}
      />
    </SlideUpPanel>
  );

  return (
    <div
      style={{
        flex: 1,
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        background: bg,
        scrollbarWidth: "none",
        fontFamily: KR_FONT,
        minHeight: "100vh",
      }}
    >
      {/* Self Blood Type Panel */}
      <SelfBloodTypePanel />
      {/* Self DOB Panel */}
      <SelfDobPanel />
      {/* Self Time Panel */}
      <SelfTimePanel />
      {/* Partner Blood Type Panel */}
      <PartnerBloodTypePanel />
      {/* Partner DOB Panel */}
      <PartnerDobPanel />
      {/* Partner Time Panel */}
      <PartnerTimePanel />

      {/* Content */}
      <div
        style={{
          padding: "24px 20px 40px",
          maxWidth: 520,
          margin: "auto 0",
          width: "100%",
        }}
      >
        {/* Step indicator */}
        <div
          style={{
            display: "flex",
            gap: 8,
            marginBottom: 28,
            justifyContent: "center",
          }}
        >
          {(["self", "partner"] as const).map((s) => (
            <div
              key={s}
              style={{
                padding: "6px 16px",
                borderRadius: 20,
                fontSize: 13,
                fontWeight: 500,
                fontFamily: KR_FONT,
                background: step === s ? COLORS.lightWarmSoft : "transparent",
                color: step === s ? COLORS.lightWarm : textMuted,
                border: `1px solid ${step === s ? COLORS.lightCardBorderActive : "transparent"}`,
                transition: "all 0.2s",
              }}
            >
              {s === "self" ? t.stepSelf : t.stepPartner}
            </div>
          ))}
        </div>

        {/* Step: Self */}
        {step === "self" && (
          <motion.div
            key="self"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
          >
            {/* Header */}
            <div style={{ textAlign: "center", marginBottom: 28 }}>
              <div
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  margin: "0 auto 14px",
                  background:
                    "linear-gradient(135deg, rgba(196,165,116,0.15), rgba(196,165,116,0.25))",
                  border: "2px solid rgba(196,165,116,0.4)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 28,
                }}
              >
                🌙
              </div>
              <h1
                style={{
                  fontSize: 20,
                  fontWeight: 700,
                  color: textColor,
                  margin: "0 0 6px",
                  fontFamily: KR_FONT,
                }}
              >
                {t.selfHeading}
              </h1>
              <p
                style={{
                  fontSize: 13,
                  color: textMuted,
                  margin: 0,
                  fontFamily: KR_FONT,
                }}
              >
                {t.selfSubheading}
              </p>
            </div>

            {/* Name Input */}
            <div style={{ marginBottom: 16 }}>
              <p
                style={{
                  fontSize: 12,
                  color: textMuted,
                  marginBottom: 8,
                  fontFamily: KR_FONT,
                }}
              >
                {t.nameLabel}
              </p>
              <input
                type="text"
                value={selfName}
                onChange={(e) => setSelfName(e.target.value)}
                placeholder={t.namePlaceholder}
                style={{
                  width: "100%",
                  height: 48,
                  borderRadius: 12,
                  border: `1px solid ${COLORS.lightInputBorder}`,
                  background: COLORS.lightInputBg,
                  color: textColor,
                  fontSize: 15,
                  padding: "0 16px",
                  fontFamily: KR_FONT,
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
            </div>

            {/* Gender Selection */}
            <div style={{ marginBottom: 20 }}>
              <p
                style={{
                  fontSize: 12,
                  color: textMuted,
                  marginBottom: 8,
                  fontFamily: KR_FONT,
                }}
              >
                {t.genderLabel}
              </p>
              <div style={{ display: "flex", gap: 10 }}>
                {t.genderOptions.map((g) => {
                  const active = selfGender === g;
                  return (
                    <motion.button
                      key={g}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setSelfGender(g)}
                      style={{
                        flex: 1,
                        height: 44,
                        borderRadius: 10,
                        border: `2px solid ${active ? COLORS.lightWarm : COLORS.lightCardBorder}`,
                        background: active
                          ? COLORS.lightWarmSoft
                          : "transparent",
                        color: active ? COLORS.lightWarm : textMuted,
                        fontSize: 14,
                        fontWeight: active ? 600 : 400,
                        cursor: "pointer",
                        fontFamily: KR_FONT,
                        transition: "all 0.2s",
                      }}
                    >
                      {g}
                    </motion.button>
                  );
                })}
              </div>
            </div>

            {/* 3-Box Cards */}
            <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
              <DestinyBox
                icon="🩸"
                labelKR={t.bloodTypeLabelKR}
                labelEN={t.bloodTypeLabelEN}
                isCompleted={!!selfBloodType}
                onClick={() => {
                  setActiveSelfBox("blood");
                  setShowSelfPanel(true);
                }}
                isDesktop={isDesktop}
              />
              <DestinyBox
                icon="📅"
                labelKR={t.birthDayLabelKR}
                labelEN={t.birthDayLabelEN}
                isCompleted={!!selfDob}
                onClick={() => {
                  setActiveSelfBox("dob");
                  setShowSelfPanel(true);
                }}
                isDesktop={isDesktop}
              />
              <DestinyBox
                icon="🕰️"
                labelKR={t.destinyTimeLabelKR}
                labelEN={t.destinyTimeLabelEN}
                isCompleted={!!selfDestinyTime}
                onClick={() => {
                  setActiveSelfBox("time");
                  setShowSelfPanel(true);
                }}
                isDesktop={isDesktop}
              />
            </div>

            {/* Continue button */}
            <PrimaryBtn
              label={t.continueButton}
              onClick={() => setStep("partner")}
              disabled={!selfReady}
              icon="→"
              isDesktop={isDesktop}
            />
          </motion.div>
        )}

        {/* Step: Partner */}
        {step === "partner" && (
          <motion.div
            key="partner"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
          >
            {/* Header */}
            <div style={{ textAlign: "center", marginBottom: 28 }}>
              <div
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  margin: "0 auto 14px",
                  background:
                    "linear-gradient(135deg, rgba(196,165,116,0.15), rgba(196,165,116,0.25))",
                  border: "2px solid rgba(196,165,116,0.4)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 28,
                }}
              >
                💫
              </div>
              <h1
                style={{
                  fontSize: 20,
                  fontWeight: 700,
                  color: textColor,
                  margin: "0 0 6px",
                  fontFamily: KR_FONT,
                }}
              >
                {t.partnerHeading}
              </h1>
              <p
                style={{
                  fontSize: 13,
                  color: textMuted,
                  margin: 0,
                  fontFamily: KR_FONT,
                }}
              >
                {t.partnerSubheading}
              </p>
            </div>

            {/* Partner Name Input */}
            <div style={{ marginBottom: 16 }}>
              <p
                style={{
                  fontSize: 12,
                  color: textMuted,
                  marginBottom: 8,
                  fontFamily: KR_FONT,
                }}
              >
                {t.partnerNameLabel}
              </p>
              <input
                type="text"
                value={partnerName}
                onChange={(e) => setPartnerName(e.target.value)}
                placeholder={t.partnerNamePlaceholder}
                style={{
                  width: "100%",
                  height: 48,
                  borderRadius: 12,
                  border: `1px solid ${COLORS.lightInputBorder}`,
                  background: COLORS.lightInputBg,
                  color: textColor,
                  fontSize: 15,
                  padding: "0 16px",
                  fontFamily: KR_FONT,
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
            </div>

            {/* Partner Gender Selection */}
            <div style={{ marginBottom: 20 }}>
              <p
                style={{
                  fontSize: 12,
                  color: textMuted,
                  marginBottom: 8,
                  fontFamily: KR_FONT,
                }}
              >
                {t.genderLabel}
              </p>
              <div style={{ display: "flex", gap: 10 }}>
                {t.genderOptions.map((g) => {
                  const active = partnerGender === g;
                  return (
                    <motion.button
                      key={g}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setPartnerGender(g)}
                      style={{
                        flex: 1,
                        height: 44,
                        borderRadius: 10,
                        border: `2px solid ${active ? COLORS.lightWarm : COLORS.lightCardBorder}`,
                        background: active
                          ? COLORS.lightWarmSoft
                          : "transparent",
                        color: active ? COLORS.lightWarm : textMuted,
                        fontSize: 14,
                        fontWeight: active ? 600 : 400,
                        cursor: "pointer",
                        fontFamily: KR_FONT,
                        transition: "all 0.2s",
                      }}
                    >
                      {g}
                    </motion.button>
                  );
                })}
              </div>
            </div>

            {/* 3-Box Cards */}
            <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
              <DestinyBox
                icon="🩸"
                labelKR={t.partnerBloodTypeLabelKR}
                labelEN={t.bloodTypeLabelEN}
                isCompleted={!!partnerBloodType}
                onClick={() => {
                  setActivePartnerBox("blood");
                  setShowPartnerPanel(true);
                }}
                isDesktop={isDesktop}
              />
              <DestinyBox
                icon="📅"
                labelKR={t.partnerBirthDayLabelKR}
                labelEN={t.birthDayLabelEN}
                isCompleted={!!partnerDob}
                onClick={() => {
                  setActivePartnerBox("dob");
                  setShowPartnerPanel(true);
                }}
                isDesktop={isDesktop}
              />
              <DestinyBox
                icon="🕰️"
                labelKR={t.partnerDestinyTimeLabelKR}
                labelEN={t.destinyTimeLabelEN}
                isCompleted={!!partnerDestinyTime}
                onClick={() => {
                  setActivePartnerBox("time");
                  setShowPartnerPanel(true);
                }}
                isDesktop={isDesktop}
              />
            </div>

            {/* Navigation */}
            <div style={{ display: "flex", gap: 12 }}>
              <motion.button
                whileTap={{ scale: 0.97 }}
                onClick={() => setStep("self")}
                style={{
                  flex: 1,
                  height: 54,
                  borderRadius: 14,
                  background: "transparent",
                  border: `1px solid ${COLORS.lightCardBorder}`,
                  color: textColor,
                  fontSize: 15,
                  fontWeight: 500,
                  fontFamily: KR_FONT,
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
              >
                {t.backButton}
              </motion.button>
              <div style={{ flex: 2 }}>
                <PrimaryBtn
                  label={t.submitButton}
                  onClick={handleSubmit}
                  disabled={!partnerReady}
                  loading={isLoading}
                  icon="💫"
                  isDesktop={isDesktop}
                />
              </div>
            </div>
          </motion.div>
        )}

        {/* Loading state */}
        {isLoading && (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <LoadingDots isDesktop={isDesktop} />
            <p
              style={{
                fontSize: 14,
                color: textMuted,
                marginTop: 16,
                fontFamily: KR_FONT,
              }}
            >
              {t.loadingText}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default KoreaCompatibility3Box;
export type { Korea3BoxData };
